import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'sonner';

// Pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ServicesPage from './pages/ServicesPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import ResourcesPage from './pages/ResourcesPage';
import ResourceDetailPage from './pages/ResourceDetailPage';
import BrandsPage from './pages/BrandsPage';
import ContactPage from './pages/ContactPage';
import BookSessionPage from './pages/BookSessionPage'; // deprecated - kept for fallback redirect
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import IntakeFormPage from './pages/IntakeFormPage';
import ProgramsPage from './pages/ProgramsPage';
import { SiteSettingsProvider } from './context/SiteSettingsContext';
import { PortalThemeProvider } from './context/PortalThemeContext';
import ProgramApplicationPage from './pages/ProgramApplicationPage';
import ProgramLandingPage from './pages/ProgramLandingPage';
import CoachingHubPage from './pages/CoachingHubPage';
import BookWithBrandPage from './pages/BookWithBrandPage';
import EmbedSchedulePage from './pages/EmbedSchedulePage';
import EmbedManagePage from './pages/EmbedManagePage';
import EmbedPackagesPage from './pages/EmbedPackagesPage';
import AdminBookingBrands from './pages/admin/AdminBookingBrands';
import AdminBrandRevenue from './pages/admin/AdminBrandRevenue';
import AdminTimeRequests from './pages/admin/AdminTimeRequests';
import AdminMemberships from './pages/admin/AdminMemberships';
import AdminCoachingPackages from './pages/admin/AdminCoachingPackages';
import AdminLinks from './pages/admin/AdminLinks';
import AdminRoadmap from './pages/admin/AdminRoadmap';
import AdminUsers from './pages/admin/AdminUsers';
import AdminActivity from './pages/admin/AdminActivity';
import AdminCalendar from './pages/admin/AdminCalendar';
import EventsPage from './pages/EventsPage';
import ScheduleSessionPage from './pages/ScheduleSessionPage';
import ManageBookingPage from './pages/ManageBookingPage';
import ContractSignPage from './pages/ContractSignPage';
import PaymentResultPage from './pages/PaymentResultPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminLeads from './pages/admin/AdminLeads';
import ShareYourStoryPage from './pages/ShareYourStoryPage';
import VisionSharePage from './pages/VisionSharePage';
import VisionInvitePage from './pages/VisionInvitePage';
import { GatedPage } from './components/portal/ToolGate';
import SocialVideoAddOnPage from './pages/SocialVideoAddOnPage';
import VideoViewerPage from './pages/VideoViewerPage';
import AdminPaymentLinks from './pages/admin/AdminPaymentLinks';
import AdminQuickSend from './pages/admin/AdminQuickSend';
import AdminStripeSetup from './pages/admin/AdminStripeSetup';
import AdminVideoSessions from './pages/admin/AdminVideoSessions';
import AdminAffiliates from './pages/admin/AdminAffiliates';
import AdminAITools from './pages/admin/AdminAITools';
import AdminCertificates from './pages/admin/AdminCertificates';
import AdminBroadcastTemplates from './pages/admin/AdminBroadcastTemplates';
import AdminComps from './pages/admin/AdminComps';
import AdminPhase2 from './pages/admin/AdminPhase2';
import AdminPaidEvents from './pages/admin/AdminPaidEvents';
import AdminWebinars from './pages/admin/AdminWebinars';
import AdminGroupSessions from './pages/admin/AdminGroupSessions';
import AdminMemberPortal from './pages/admin/AdminMemberPortal';
import PaidEventsPage from './pages/PaidEventsPage';
import PaidEventDetailPage from './pages/PaidEventDetailPage';
import WebinarRoomPage from './pages/WebinarRoomPage';
import AdminCoaches from './pages/admin/AdminCoaches';
import AdminJumpstartSessions from './pages/admin/AdminJumpstartSessions';import CoachesPage from './pages/CoachesPage';
import CoachProfilePage from './pages/CoachProfilePage';
import CoachApplyPage from './pages/CoachApplyPage';
import CoachDashboard from './pages/CoachDashboard';
import AdminResources from './pages/admin/AdminResources';
import AffiliatePortalDashboard, { AffiliatePortalLogin, AffiliatePortalVerify } from './pages/AffiliatePortal';
import AdminLeadDetail from './pages/admin/AdminLeadDetail';
import AdminResourceDownloads from './pages/admin/AdminResourceDownloads';
import AdminBookings from './pages/admin/AdminBookings';
import AdminClients from './pages/admin/AdminClients';
import AdminClientDetail from './pages/admin/AdminClientDetail';
import AdminPrograms from './pages/admin/AdminPrograms';
import AdminEvents from './pages/admin/AdminEvents';
import AdminEventReminders from './pages/admin/AdminEventReminders';
import AdminBlasts from './pages/admin/AdminBlasts';
import AdminSiteManager from './pages/admin/AdminSiteManager';
import AdminSubscribers from './pages/admin/AdminSubscribers';
import SubscribeConfirmPage from './pages/SubscribeConfirmPage';
import AdminServices from './pages/admin/AdminServices';
import AdminAutomation from './pages/admin/AdminAutomation';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminScheduling from './pages/admin/AdminScheduling';
import AdminContracts from './pages/admin/AdminContracts';
import ClientDashboard from './pages/portal/ClientDashboard';
import ClientClarityHub from './pages/portal/ClientClarityHub';
import ClientTasks from './pages/portal/ClientTasks';
import ClientGoals from './pages/portal/ClientGoals';
import ClientResources from './pages/portal/ClientResources';
import ClientSessions from './pages/portal/ClientSessions';
import ClientSessionDetail from './pages/portal/ClientSessionDetail';
import ClientTraining from './pages/portal/ClientTraining';
import ClientGroupSessionDetail from './pages/portal/ClientGroupSessionDetail';
import ClientSettings from './pages/portal/ClientSettings';
import ClientPrograms from './pages/portal/ClientPrograms';
import ClientBook from './pages/portal/ClientBook';
import ClientStudio from './pages/portal/ClientStudio';
import ClientFlyerStudio from './pages/portal/ClientFlyerStudio';
import ClientContentIntelligence from './pages/portal/ClientContentIntelligence';
import ClientStudioResources from './pages/portal/ClientStudioResources';
import StudioPlans from './pages/StudioPlans';
import StudioLanding from './pages/StudioLanding';
import StudioStart from './pages/StudioStart';
import ChooseWorkspace from './pages/ChooseWorkspace';
import ClientAffiliates from './pages/portal/ClientAffiliates';
import ClientCoaches from './pages/portal/ClientCoaches';
import CoachJumpstartSessions from './pages/portal/CoachJumpstartSessions';
import ClientBlueprint from './pages/portal/ClientBlueprint';
import { TermsPage, PrivacyPage } from './pages/LegalPages';
import ScrollToTop from './components/ScrollToTop';
import VerifyEmailBanner from './components/VerifyEmailBanner';
import ViewAsBanner from './components/ViewAsBanner';
const ChallengeApp = React.lazy(() => import('./challenge/App'));
import VerifyEmailPage from './pages/VerifyEmailPage';
import { KnowledgeBasePage, KnowledgeBaseArticlePage } from './pages/KnowledgeBasePages';
import AdminKnowledgeBase from './pages/admin/AdminKnowledgeBase';
import AdminMaintenance from './pages/admin/AdminMaintenance';
import AdminDeployReadiness from './pages/admin/AdminDeployReadiness';
import AdminEarnings from './pages/admin/AdminEarnings';
import SocialStudio from './pages/admin/SocialStudio';
import NonprofitAccelerator from './pages/admin/NonprofitAccelerator';
import ClarityHub from './pages/admin/ClarityHub';
import CombinedInterview from './pages/admin/CombinedInterview';
import AdminBlockList from './pages/admin/AdminBlockList';
import WebsiteVisionBuilder from './pages/admin/WebsiteVisionBuilder';
import NonprofitProgress from './pages/admin/NonprofitProgress';
import AdminQrAnalytics from './pages/admin/AdminQrAnalytics';
import FlyerStudio from './pages/admin/FlyerStudio';
import ContentIntelligence from './pages/admin/ContentIntelligence';
import StudioResources from './pages/admin/StudioResources';
import AdminIntelligenceSettings from './pages/admin/AdminIntelligenceSettings';
import AdminSharedPlans from './pages/admin/AdminSharedPlans';
import SharedScriptPage from './pages/SharedScriptPage';
import StudioInvite from './pages/StudioInvite';
import AuthCallback from './pages/AuthCallback';
import GoogleCallback from './pages/GoogleCallback';
import { getTurnstileToken } from './lib/turnstile';

import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
axios.defaults.withCredentials = true;

// --- Silent session refresh ---------------------------------------------
// When the short-lived access_token cookie expires, transparently mint a new
// one from the long-lived refresh_token cookie and retry the original request,
// so users stay logged in across the whole app (portal + main site) until they
// explicitly log out. A single shared refresh promise prevents stampedes/loops.
let _refreshPromise = null;
const _noRefreshPaths = ['/api/auth/login', '/api/auth/refresh', '/api/auth/register', '/api/auth/logout'];

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || '';
    const skip = _noRefreshPaths.some((p) => url.includes(p));

    if (status === 401 && !original._retry && !skip) {
      original._retry = true;
      try {
        if (!_refreshPromise) {
          _refreshPromise = axios.post(`${BACKEND_URL}/api/auth/refresh`).finally(() => { _refreshPromise = null; });
        }
        await _refreshPromise;
        return axios(original); // retry the original request with the new cookie
      } catch (e) {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if ((window.location.hash?.includes('session_id=') && !window.location.pathname.startsWith('/challenge')) || window.location.pathname === '/auth/google') {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, remember_me = false) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/login`, { email, password, remember_me });
    // If 2FA is required, do NOT set user yet - the LoginPage will run /api/auth/2fa/verify next
    if (response.data?.mfa_required) {
      return response.data;
    }
    setUser(response.data);
    return response.data;
  };

  const register = async (name, email, password, terms_accepted = true, extra = {}) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/register`, {
      name, email, password, terms_accepted,
      first_name: extra.first_name || null,
      last_name: extra.last_name || null,
      phone: extra.phone || null,
      turnstile_token: await getTurnstileToken('register'),
    });
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try { await axios.post(`${BACKEND_URL}/api/auth/logout`); } finally { setUser(null); }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    setUser,
    checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route for Admin
const ProtectedAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#475569]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user.role !== 'admin' && user.role !== 'super_admin') {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Protected Route for Client Portal
const ProtectedClientRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Protected Route for Social Studio — admins OR studio-approved (paying) clients
const ProtectedStudioRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const allowed = user.role === 'admin' || user.role === 'super_admin' || user.is_studio_approved === true;
  if (!allowed) {
    return <Navigate to="/portal/studio" replace />;
  }

  return children;
};
function AppRouter() {
  const location = useLocation();
  const lastTrackedPath = React.useRef(null);

  // Visit tracking: ping backend on every public route change (skip /admin/* and /portal/* and /embed/*)
  // Ref-guarded to avoid double-fires from React StrictMode in dev.
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/admin') || path.startsWith('/portal') || path.startsWith('/embed')) return;
    if (lastTrackedPath.current === path) return;
    lastTrackedPath.current = path;
    axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/track/visit`, {
      path,
      referrer: document.referrer || null,
    }).catch(() => {});
  }, [location.pathname]);

  // Check URL fragment for session_id (OAuth callback)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=') && !location.pathname.startsWith('/challenge')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/services/:slug" element={<ServiceDetailPage />} />
      <Route path="/resources" element={<ResourcesPage />} />
      <Route path="/resources/:slug" element={<ResourceDetailPage />} />
      <Route path="/brands" element={<BrandsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/book-session" element={<Navigate to="/free-jumpstart-session" replace />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
      <Route path="/knowledge-base/:slug" element={<KnowledgeBaseArticlePage />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/choose-workspace" element={<ChooseWorkspace />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="/studio-invite/:token" element={<StudioInvite />} />
      <Route path="/studio" element={<StudioLanding />} />
      <Route path="/studio/start" element={<ProtectedClientRoute><StudioStart /></ProtectedClientRoute>} />
      <Route path="/intake" element={<IntakeFormPage />} />
      <Route path="/programs" element={<ProgramsPage />} />
      <Route path="/program-application" element={<ProgramApplicationPage />} />
      <Route path="/free-jumpstart-session" element={<ProgramLandingPage slug="free-jumpstart-session" />} />
      <Route path="/book/nonprofit-jumpstart" element={<ScheduleSessionPage variant="nonprofit" />} />
      <Route path="/free-health-jumpstart" element={<ScheduleSessionPage variant="health" />} />
      <Route path="/free-life-jumpstart" element={<ScheduleSessionPage variant="life" />} />
      <Route path="/business-strategy-session" element={<ProgramLandingPage slug="business-strategy-session" />} />
      <Route path="/business-jumpstart-accelerator" element={<ProgramLandingPage slug="business-jumpstart-accelerator" />} />
      <Route path="/business-growth-partnership" element={<ProgramLandingPage slug="business-growth-partnership" />} />
      <Route path="/visionary-mentorship" element={<ProgramLandingPage slug="visionary-mentorship" />} />
      <Route path="/events" element={<EventsPage />} />
      <Route path="/schedule" element={<Navigate to="/free-jumpstart-session" replace />} />
      <Route path="/manage-booking" element={<ManageBookingPage />} />
      <Route path="/subscribe/confirm" element={<SubscribeConfirmPage />} />
      <Route path="/share-your-story" element={<ShareYourStoryPage />} />
      <Route path="/vision/:token" element={<VisionSharePage />} />
      <Route path="/vision-start/:token" element={<VisionInvitePage />} />
      <Route path="/add-ons/social-video-manager" element={<SocialVideoAddOnPage />} />
      <Route path="/v/:token" element={<VideoViewerPage />} />
      <Route path="/affiliate-portal/login" element={<AffiliatePortalLogin />} />
      <Route path="/affiliate-portal/verify" element={<AffiliatePortalVerify />} />
      <Route path="/affiliate-portal" element={<AffiliatePortalDashboard />} />

      {/* Multi-brand coaching scheduler */}
      <Route path="/coaching" element={<CoachingHubPage />} />
      <Route path="/coaching/:slug" element={<BookWithBrandPage />} />
      <Route path="/embed/schedule" element={<EmbedSchedulePage />} />
      <Route path="/embed/manage" element={<EmbedManagePage />} />
      <Route path="/embed/packages" element={<EmbedPackagesPage />} />
      <Route path="/contract/sign" element={<ContractSignPage />} />
      <Route path="/payment/success" element={<PaymentResultPage />} />
      <Route path="/payment/cancel" element={<PaymentResultPage />} />
      
      {/* Client Portal Routes */}
      <Route path="/portal" element={<ProtectedClientRoute><ClientDashboard /></ProtectedClientRoute>} />
      <Route path="/portal/clarity-hub" element={<ProtectedClientRoute><ClientClarityHub /></ProtectedClientRoute>} />
      <Route path="/portal/tasks" element={<ProtectedClientRoute><ClientTasks /></ProtectedClientRoute>} />
      <Route path="/portal/goals" element={<ProtectedClientRoute><ClientGoals /></ProtectedClientRoute>} />
      <Route path="/portal/resources" element={<ProtectedClientRoute><ClientResources /></ProtectedClientRoute>} />
      <Route path="/portal/sessions" element={<ProtectedClientRoute><ClientSessions /></ProtectedClientRoute>} />
      <Route path="/portal/sessions/:sessionId" element={<ProtectedClientRoute><ClientSessionDetail /></ProtectedClientRoute>} />
      <Route path="/portal/training" element={<ProtectedClientRoute><GatedPage tool="live_training"><ClientTraining /></GatedPage></ProtectedClientRoute>} />
      <Route path="/portal/group-sessions/:groupSessionId" element={<ProtectedClientRoute><ClientGroupSessionDetail /></ProtectedClientRoute>} />
      <Route path="/portal/settings" element={<ProtectedClientRoute><ClientSettings /></ProtectedClientRoute>} />
      <Route path="/portal/programs" element={<ProtectedClientRoute><ClientPrograms /></ProtectedClientRoute>} />
      <Route path="/portal/book" element={<ProtectedClientRoute><ClientBook /></ProtectedClientRoute>} />
      <Route path="/portal/studio" element={<ProtectedClientRoute><ClientStudio /></ProtectedClientRoute>} />
      <Route path="/portal/studio/flyer" element={<ProtectedClientRoute><ClientFlyerStudio /></ProtectedClientRoute>} />
      <Route path="/portal/studio/intelligence" element={<ProtectedClientRoute><ClientContentIntelligence /></ProtectedClientRoute>} />
      <Route path="/portal/studio/resources" element={<ProtectedClientRoute><ClientStudioResources /></ProtectedClientRoute>} />
      <Route path="/portal/studio/plans" element={<ProtectedStudioRoute><StudioPlans /></ProtectedStudioRoute>} />
      <Route path="/portal/affiliates" element={<ProtectedClientRoute><ClientAffiliates /></ProtectedClientRoute>} />
      <Route path="/portal/coaches" element={<ProtectedClientRoute><ClientCoaches /></ProtectedClientRoute>} />
      <Route path="/portal/jumpstart-sessions" element={<ProtectedClientRoute><CoachJumpstartSessions /></ProtectedClientRoute>} />
      <Route path="/portal/my-blueprint" element={<ProtectedClientRoute><GatedPage tool="blueprint"><ClientBlueprint /></GatedPage></ProtectedClientRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute>} />
      <Route path="/admin/leads" element={<ProtectedAdminRoute><AdminLeads /></ProtectedAdminRoute>} />
      <Route path="/admin/leads/:leadId" element={<ProtectedAdminRoute><AdminLeadDetail /></ProtectedAdminRoute>} />
      <Route path="/admin/resource-downloads" element={<ProtectedAdminRoute><AdminResourceDownloads /></ProtectedAdminRoute>} />
      <Route path="/admin/bookings" element={<ProtectedAdminRoute><AdminBookings /></ProtectedAdminRoute>} />
      <Route path="/admin/clients" element={<ProtectedAdminRoute><AdminClients /></ProtectedAdminRoute>} />
      <Route path="/admin/clients/:clientId" element={<ProtectedAdminRoute><AdminClientDetail /></ProtectedAdminRoute>} />
      <Route path="/admin/programs" element={<ProtectedAdminRoute><AdminPrograms /></ProtectedAdminRoute>} />
      <Route path="/admin/events" element={<ProtectedAdminRoute><AdminEvents /></ProtectedAdminRoute>} />
      <Route path="/admin/event-reminders" element={<ProtectedAdminRoute><AdminEventReminders /></ProtectedAdminRoute>} />
      <Route path="/admin/blasts" element={<ProtectedAdminRoute><AdminBlasts /></ProtectedAdminRoute>} />
      <Route path="/admin/site-manager" element={<ProtectedAdminRoute><AdminSiteManager /></ProtectedAdminRoute>} />
      <Route path="/admin/services" element={<ProtectedAdminRoute><AdminServices /></ProtectedAdminRoute>} />
      <Route path="/admin/automation" element={<ProtectedAdminRoute><AdminAutomation /></ProtectedAdminRoute>} />
      <Route path="/admin/analytics" element={<ProtectedAdminRoute><AdminAnalytics /></ProtectedAdminRoute>} />
      <Route path="/admin/scheduling" element={<ProtectedAdminRoute><AdminScheduling /></ProtectedAdminRoute>} />
      <Route path="/admin/contracts" element={<ProtectedAdminRoute><AdminContracts /></ProtectedAdminRoute>} />
      <Route path="/admin/booking-brands" element={<ProtectedAdminRoute><AdminBookingBrands /></ProtectedAdminRoute>} />
      <Route path="/admin/brand-revenue" element={<ProtectedAdminRoute><AdminBrandRevenue /></ProtectedAdminRoute>} />
      <Route path="/admin/time-requests" element={<ProtectedAdminRoute><AdminTimeRequests /></ProtectedAdminRoute>} />
      <Route path="/admin/coaching-packages" element={<ProtectedAdminRoute><AdminCoachingPackages /></ProtectedAdminRoute>} />
      <Route path="/challenge/*" element={<React.Suspense fallback={null}><ChallengeApp /></React.Suspense>} />
      <Route path="/admin/memberships" element={<ProtectedAdminRoute><AdminMemberships /></ProtectedAdminRoute>} />
      <Route path="/admin/links" element={<ProtectedAdminRoute><AdminLinks /></ProtectedAdminRoute>} />
      <Route path="/admin/roadmap" element={<ProtectedAdminRoute><AdminRoadmap /></ProtectedAdminRoute>} />
      <Route path="/admin/users" element={<ProtectedAdminRoute><AdminUsers /></ProtectedAdminRoute>} />
      <Route path="/admin/activity" element={<ProtectedAdminRoute><AdminActivity /></ProtectedAdminRoute>} />
      <Route path="/admin/knowledge-base" element={<ProtectedAdminRoute><AdminKnowledgeBase /></ProtectedAdminRoute>} />
      <Route path="/admin/maintenance" element={<ProtectedAdminRoute><AdminMaintenance /></ProtectedAdminRoute>} />
      <Route path="/admin/deploy" element={<ProtectedAdminRoute><AdminDeployReadiness /></ProtectedAdminRoute>} />
      <Route path="/admin/earnings" element={<ProtectedAdminRoute><AdminEarnings /></ProtectedAdminRoute>} />
      <Route path="/admin/social-studio" element={<ProtectedStudioRoute><SocialStudio /></ProtectedStudioRoute>} />
      <Route path="/admin/flyer-studio" element={<ProtectedStudioRoute><FlyerStudio /></ProtectedStudioRoute>} />
      <Route path="/admin/intelligence" element={<ProtectedStudioRoute><ContentIntelligence /></ProtectedStudioRoute>} />
      <Route path="/admin/studio-resources" element={<ProtectedStudioRoute><StudioResources /></ProtectedStudioRoute>} />
      <Route path="/admin/intelligence-settings" element={<ProtectedAdminRoute><AdminIntelligenceSettings /></ProtectedAdminRoute>} />
      <Route path="/admin/social-studio/shares" element={<ProtectedAdminRoute><AdminSharedPlans /></ProtectedAdminRoute>} />
      <Route path="/admin/clarity-hub" element={<ProtectedAdminRoute><ClarityHub /></ProtectedAdminRoute>} />
      <Route path="/admin/combined-interview" element={<ProtectedAdminRoute><CombinedInterview /></ProtectedAdminRoute>} />
      <Route path="/admin/website-vision" element={<ProtectedAdminRoute><WebsiteVisionBuilder /></ProtectedAdminRoute>} />
      <Route path="/admin/block-list" element={<ProtectedAdminRoute><AdminBlockList /></ProtectedAdminRoute>} />
      <Route path="/admin/nonprofit-accelerator" element={<ProtectedAdminRoute><NonprofitAccelerator /></ProtectedAdminRoute>} />
      <Route path="/admin/nonprofit-accelerator/progress/:projectId" element={<ProtectedAdminRoute><NonprofitProgress /></ProtectedAdminRoute>} />
      <Route path="/admin/qr-analytics" element={<ProtectedAdminRoute><AdminQrAnalytics /></ProtectedAdminRoute>} />
      <Route path="/np/progress/:token" element={<NonprofitProgress />} />
      <Route path="/admin/payment-links" element={<ProtectedAdminRoute><AdminPaymentLinks /></ProtectedAdminRoute>} />
      <Route path="/admin/quick-send" element={<ProtectedAdminRoute><AdminQuickSend /></ProtectedAdminRoute>} />
      <Route path="/admin/stripe-setup" element={<ProtectedAdminRoute><AdminStripeSetup /></ProtectedAdminRoute>} />
      <Route path="/admin/video-sessions" element={<ProtectedAdminRoute><AdminVideoSessions /></ProtectedAdminRoute>} />
      <Route path="/admin/affiliates" element={<ProtectedAdminRoute><AdminAffiliates /></ProtectedAdminRoute>} />
      <Route path="/admin/ai-tools" element={<ProtectedAdminRoute><AdminAITools /></ProtectedAdminRoute>} />
      <Route path="/admin/certificates" element={<ProtectedAdminRoute><AdminCertificates /></ProtectedAdminRoute>} />
      <Route path="/admin/email-templates" element={<ProtectedAdminRoute><AdminBroadcastTemplates /></ProtectedAdminRoute>} />
      <Route path="/admin/comp-sessions" element={<ProtectedAdminRoute><AdminComps /></ProtectedAdminRoute>} />
      <Route path="/admin/workflows" element={<ProtectedAdminRoute><AdminPhase2 /></ProtectedAdminRoute>} />
      <Route path="/admin/ticketed-events" element={<ProtectedAdminRoute><AdminPaidEvents /></ProtectedAdminRoute>} />
      <Route path="/admin/webinars" element={<ProtectedAdminRoute><AdminWebinars /></ProtectedAdminRoute>} />
      <Route path="/admin/group-sessions" element={<ProtectedAdminRoute><AdminGroupSessions /></ProtectedAdminRoute>} />
      <Route path="/admin/member-portal" element={<ProtectedAdminRoute><AdminMemberPortal /></ProtectedAdminRoute>} />
      <Route path="/admin/coaches" element={<ProtectedAdminRoute><AdminCoaches /></ProtectedAdminRoute>} />
      <Route path="/admin/jumpstart-sessions" element={<ProtectedAdminRoute><AdminJumpstartSessions /></ProtectedAdminRoute>} />
      <Route path="/intake/:slug" element={<IntakeFormPage />} />
      <Route path="/tickets" element={<PaidEventsPage />} />
      <Route path="/tickets/:slug" element={<PaidEventDetailPage />} />
      <Route path="/webinar/:slug" element={<WebinarRoomPage />} />
      <Route path="/coaches" element={<CoachesPage />} />
      <Route path="/coaches/apply" element={<CoachApplyPage />} />
      <Route path="/coaches/:slug" element={<CoachProfilePage />} />
      <Route path="/coach/dashboard" element={<CoachDashboard />} />
      <Route path="/admin/resources" element={<ProtectedAdminRoute><AdminResources /></ProtectedAdminRoute>} />
      <Route path="/admin/subscribers" element={<ProtectedAdminRoute><AdminSubscribers /></ProtectedAdminRoute>} />
      <Route path="/share/script/:token" element={<SharedScriptPage />} />
      <Route path="/admin/calendar" element={<ProtectedAdminRoute><AdminCalendar /></ProtectedAdminRoute>} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Remove "Made with Emergent" platform badge (inline styles with !important require JS removal)
  useEffect(() => {
    const remove = () => {
      const el = document.getElementById('emergent-badge');
      if (el) el.remove();
    };
    remove();
    const observer = new MutationObserver(remove);
    observer.observe(document.body, { childList: true, subtree: false });
    return () => observer.disconnect();
  }, []);

  // Affiliate referral tracking: capture ?ref=CODE in URL, persist in cookie + ping API
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      if (ref) {
        const ninety = 90 * 24 * 60 * 60;
        document.cookie = `dca_ref=${encodeURIComponent(ref)}; path=/; max-age=${ninety}; SameSite=Lax`;
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        if (apiUrl) {
          fetch(`${apiUrl}/api/affiliates/track`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ref }),
          }).catch(() => { /* silent */ });
        }
      }
    } catch { /* ignore */ }
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <SiteSettingsProvider>
          <PortalThemeProvider>
            <ScrollToTop />
            <ViewAsBanner />
            <VerifyEmailBanner />
            <Toaster position="top-right" richColors />
            <AppRouter />
          </PortalThemeProvider>
        </SiteSettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
